'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import { PortalObjectReferenceCard } from '../shared/PortalObjectReferenceCard';
import { ToolInlineStatusLine } from '../shared/ToolInlineStatusLine';

type SkillDraftUpdateCardProps = {
  ctx: CardRenderContext;
};

function SkillDraftUpdateCard({ ctx }: SkillDraftUpdateCardProps) {
  const t = useTranslations('designSystem.chat.objectReference');
  const status = ctx.toolCallData.status;
  const isRunning = status === 'started' || status === 'in-progress';
  const isError = status === 'error' || Boolean(ctx.toolCallData.error);
  const canShowDraftReference =
    status === 'completed' && Boolean(ctx.skillDraftReference);

  if (isRunning) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.runningText ?? ctx.labelPresent}
      />
    );
  }

  if (isError) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.toolCallData.error ?? ctx.labelPast}
        isError
      />
    );
  }

  if (!canShowDraftReference || !ctx.skillDraftReference) {
    return null;
  }

  return (
    <PortalObjectReferenceCard
      objectType="skill-draft"
      objectId={ctx.skillDraftReference.id}
      title={ctx.skillDraftReference.label}
      prefix={t('updatedSkillDraft')}
      onOpenObject={ctx.onOpenObject}
      navigationTarget={ctx.skillDraftReference}
    />
  );
}

export const renderSkillDraftUpdateCard = (
  ctx: CardRenderContext,
): ReactNode => <SkillDraftUpdateCard ctx={ctx} />;
